'use client';

/**
 * Hook pour gérer le logout de l'utilisateur
 * 
 * Supporte deux modes de logout :
 * 1. **Logout global** (défaut) : Termine la session Azure B2C et déconnecte l'utilisateur
 *    de TOUTES les applications UIC, puis redirige vers la page d'accueil
 * 2. **Logout local** : Clear uniquement les cookies locaux, garde la session Azure B2C
 *    active pour permettre un re-login rapide via SSO, puis redirige vers la page d'accueil
 * 
 * @see docs_sso/GUIDE_SSO_AZURE_B2C_UIC.md - Section "Logout"
 */

export interface LogoutOptions {
  /**
   * Logout local uniquement (garde session Azure B2C)
   * @default false - Logout global par défaut
   */
  localOnly?: boolean;
}

/**
 * Hook useLogout avec support logout global/local
 * 
 * @example
 * ```tsx
 * const { logout } = useLogout();
 * 
 * // Logout global (défaut) - déconnecte de toutes les apps UIC
 * <button onClick={() => logout()}>Logout partout</button>
 * 
 * // Logout local - garde session Azure B2C
 * <button onClick={() => logout({ localOnly: true })}>Logout de cette app</button>
 * ```
 */
export function useLogout() {
  const logout = async (options: LogoutOptions = {}) => {
    const { localOnly = false } = options;

    console.log('[useLogout] Logout requested:', { localOnly });

    // Construire l'URL de logout
    const logoutUrl = `/api/auth/logout-b2c${localOnly ? '?local=true' : ''}`;
    
    // Rediriger vers l'endpoint de logout
    // L'endpoint va clear les cookies et rediriger vers Azure B2C (si global) ou /signin (si local)
    window.location.replace(logoutUrl);
  };

  return { logout };
}

